import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Form, getIn, withFormik } from 'formik';
import * as Yup from 'yup';
import { Button, Card, Col, Row } from 'react-bootstrap';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { indexSelectEmployeesRequest } from '../../requests/employees';
import { FormikRangePicker, FormikSelect, InputSelect } from '../../components';
import { debounceIndexBranchOfficesRequest } from '../../requests/branchOffices';
import { debounceIndexShiftsRequest } from '../../requests/shifts';
import { debounceIndexJobTitleRequest } from '../../requests/jobTitles';

const ALL_SELECTED_LABEL = 'Todos';
const debounceEmployeesSelect = AwesomeDebouncePromise(indexSelectEmployeesRequest, 300);
const debounceCreatedBySelect = AwesomeDebouncePromise(indexSelectEmployeesRequest, 300);

const ShiftAssignmentsFilters = props => {
  const { errors, touched, setFieldValue, values, rrhh } = props;
  const { currentEmployee } = useSelector(state => state.auth);
  const [shifts, setShifts] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const loading = !rrhh && !currentEmployee;
  const [branchOffices, setBranchOffices] = useState([]);
  const dispatch = useDispatch();

  const fetchEmployeeSelect = (inputValue, callback) => {
    if (!rrhh && !currentEmployee) callback([]);

    const params = {
      active: true,
      filter_name: inputValue,
      sort_column: 'name',
      is_dt: false,
      display_length: 100
    };

    if (rrhh === false) {
      params.employees_belong_boss = currentEmployee;
    }

    debounceEmployeesSelect({
      dispatch,
      params,
      successCallback: response => {
        callback(response.data.data);
      }
    });
  };

  const fetchCreatedBySelect = (inputValue, callback) => {
    const params = {
      active: true,
      filter_name: inputValue,
      sort_column: 'name',
      is_dt: false,
      display_length: 100,
      active_contracts: true
    };

    debounceCreatedBySelect({
      dispatch,
      params,
      successCallback: response => {
        callback(response.data.data);
      }
    });
  };

  const fetchBranchOffices = () => {
    debounceIndexBranchOfficesRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        paginate: false
      },
      successCallback: response => {
        setBranchOffices(response.data.data);
      }
    });
  };

  const fetchShifts = () => {
    debounceIndexShiftsRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        paginate: false
      },
      successCallback: response => setShifts(response.data.data)
    });
  };

  const fetchJobTitles = () => {
    debounceIndexJobTitleRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        paginate: false
      },
      successCallback: response => {
        setJobTitles(response.data.data);
      }
    });
  };

  const fetchData = () => {
    fetchShifts();
    fetchJobTitles();
    fetchBranchOffices();
  };

  const hasAllSelection = (groupSelection = []) => {
    return groupSelection.some(option => option.value === ALL_SELECTED_LABEL);
  };

  const handleDisabledSelection = (options, selectedOptions) => {
    if (hasAllSelection(selectedOptions)) {
      return true;
    }
    if (selectedOptions.length === 0) return false;
    return options.value === ALL_SELECTED_LABEL;
  };

  const handleNewMulti = (data, field, allowEmpty = false) => {
    const newData = data.map(element => element.value);
    if (newData.length && newData.includes(ALL_SELECTED_LABEL)) {
      newData.push('');
      return;
    }

    if (allowEmpty && !newData.length) newData.push('');
    setFieldValue(field, newData);
  };

  useEffect(fetchData, []);

  useEffect(() => {
    if (rrhh) return;
    setFieldValue('filterBelongBoss', currentEmployee);
  }, [currentEmployee, setFieldValue, rrhh]);

  return (
    <Form>
      <Card>
        <Row>
          <Col className="mt-2">
            <Card className="card-dt">
              <Col className="top-header-green-dt card-header-title-dt">Rango de Fechas o Colaborador(es)</Col>
              <Card.Body className="div-content card-body-padding">
                <Row>
                  <Col sm={12} md={4} xs={12}>
                    <Field name="date_range">
                      {({ field }) => (
                        <FormikRangePicker
                          {...field}
                          abbr
                          startDateName="date_from"
                          endDateName="date_to"
                          startDate={values.date_from}
                          endDate={values.date_to}
                          showClearDates
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                  <Col sm={12} md={4} xs={12}>
                    <Field name="employees">
                      {({ field }) => (
                        <InputSelect
                          {...field}
                          key={`${rrhh}-${currentEmployee}`}
                          label="Colaborador(es)"
                          isMulti
                          placeholder="Seleccione colaboradores"
                          value={values.employees}
                          request={fetchEmployeeSelect}
                          onChange={data => {
                            handleNewMulti(data || [], 'filter_employee_ids', true);
                            setFieldValue(field.name, data || []);
                            setFieldValue('filter_id', '');
                          }}
                          isOptionDisabled={handleDisabledSelection}
                          isClearable
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                  <Col sm={12} md={4} xs={12}>
                    <Field name="employeeCreatedBy">
                      {({ field }) => (
                        <InputSelect
                          {...field}
                          label="Creado por"
                          placeholder="Seleccione Creador"
                          value={values.employeeCreatedBy}
                          request={fetchCreatedBySelect}
                          onChange={data => {
                            setFieldValue(field.name, data || '');
                            setFieldValue('filter_created_by', data ? data.value : '');
                          }}
                          isClearable
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card className="card-dt">
              <Col className="top-header-green-dt card-header-title-dt">Búsqueda Grupal</Col>
              <Card.Body className="div-content card-body-padding">
                <Row>
                  <Col sm={12} md={4} xs={12} className="mt-2">
                    <Field name="branch_offices">
                      {({ field }) => (
                        <FormikSelect
                          {...field}
                          label="Lugar de Prestación de Servicios"
                          menuPlacement="top"
                          placeholder="Seleccione Lugar de Prestación de Servicios"
                          options={branchOffices}
                          isClearable
                          isDisabled={loading}
                          onChange={data => {
                            const value = data ? data.value : '';
                            setFieldValue(field.name, value);
                          }}
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                  <Col sm={12} md={4} xs={12} className="mt-2">
                    <Field name="shifts">
                      {({ field }) => (
                        <FormikSelect
                          {...field}
                          label="Turnos"
                          menuPlacement="top"
                          placeholder="Seleccione Turno"
                          options={shifts}
                          isClearable
                          isDisabled={loading}
                          onChange={data => {
                            const value = data ? data.value : '';
                            setFieldValue(field.name, value);
                          }}
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                  <Col sm={12} md={4} xs={12} className="mt-2">
                    <Field name="job_title">
                      {({ field }) => (
                        <FormikSelect
                          {...field}
                          label="Cargos"
                          menuPlacement="top"
                          placeholder="Seleccione Cargo"
                          options={jobTitles}
                          isClearable
                          isDisabled={loading}
                          onChange={data => {
                            const value = data ? data.value : '';
                            setFieldValue(field.name, value);
                          }}
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Card>
      <Row className="d-flex justify-content-end mt-4">
        <Col md={2}>
          <Button className="btn-block" type="submit" disabled={loading}>
            Filtrar
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

const validationSchema = Yup.object().shape({
  date_range: Yup.array().when('filter_offday', {
    is: true,
    then: Yup.array().rangedate(true)
  })
});

const setInitialValues = () => {
  return {
    date_from: '',
    date_to: '',
    date_range: [],
    employees: [],
    filter_created_by: '',
    employeeCreatedBy: ''
  };
};

const handleSubmit = (values, { props }) => {
  const { formRequest } = props;
  const submitValues = { ...values };
  formRequest(submitValues);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: false
})(ShiftAssignmentsFilters);
